import { useSelector, useDispatch } from 'react-redux';
import {
  deleteStopsFavorites,
  stopsFavoritesState,
  addStopsFavorites,
  deleteLinesFavorites,
  linesFavoriteState,
  addLinesFavorites,
} from 'redux/slices/favoritesSlice';
import {
  poisFavoritesState,
  deletePoisFavorites,
  addFavoritePois,
  directionsFavoritesState,
  deleteDirectionsFavorites,
  eventsFavoritesState,
  addFavoriteEvents,
  deleteEventsFavorites,
  updateMenuFavorites,
  updateFavoritesDirections,
} from 'redux/slices/favoritesSlice';
import {
  updateContextualMessage,
  updateShowLoading,
  updateSecondaryContextualMessage,
} from 'redux/slices/contextualSlice';
import {
  usePostFavoritesMutation,
  useDeleteFavoritesMutation,
} from 'redux/services/favoritesService';
import { useTranslate } from 'context/languageContext';
import { useLazyGetFavoritesQuery } from 'redux/services/favoritesService';
import { TYPES_MARKERS } from 'utils/constants';
import useAnalytics from 'hooks/useAnalytics/useAnalytics';

export const useFavorites = () => {
  const [GetFavorites] = useLazyGetFavoritesQuery();
  const t = useTranslate();
  const dispatch = useDispatch();
  const favoriteStops = useSelector(stopsFavoritesState);
  const favoriteLines = useSelector(linesFavoriteState);
  const favoritePois = useSelector(poisFavoritesState);
  const favoriteEvents = useSelector(eventsFavoritesState);

  const favoriteDestinations = useSelector(directionsFavoritesState);
  const [DeleteFavorites] = useDeleteFavoritesMutation();
  const [PostFavorites] = usePostFavoritesMutation();
  const {logEventAnalytics} = useAnalytics();

  const textFormater = (param: string): string => {
    if (!param) return param;

    return param
      .trim()
      .slice(0, 100);
      // .replace(/['|/]/g, '') // Elimina '
      // .replace(/['/«]/g, '') // Elimina «
      // .replace(/['/»]/g, '') // Elimina »
  };


  const favorites = {
    stops: {
      getId: () => 'stopId',
      deleteErrorMsg: t('lb_borrar_parada_error_msj'),
      addErrorMsg: t('lb_agregar_parada_error_msj'),
      deleteSuccessMsg: t('lb_borrar_parada_exito_msj'),
      secondarySuccessMsg: t('lb_borrar_parada_exito_msj_secundario'),
      contextualRedirect: () => {
        dispatch(updateMenuFavorites(0));
      },
      addSuccesMsg: t('lb_agregar_parada_exito_msj'),
      store: favoriteStops,
      formater: (id, name, alias, categoryId) => {
        return {
          stopId: id,
          alias: alias !== undefined ? alias : name,
          categoryId: categoryId !== undefined ? categoryId : 0,
        };
      },
      deleteStore: (id) => dispatch(deleteStopsFavorites(id)),
      addStore: (id) => dispatch(addStopsFavorites(id)),
    },
    lines: {
      getId: () => 'routeId',
      deleteErrorMsg: t('lb_borrar_linea_error_msj'),
      addErrorMsg: t('lb_agregar_linea_error_msj'),
      deleteSuccessMsg: t('lb_borrar_linea_exito_msj'),
      secondarySuccessMsg: t('lb_borrar_linea_exito_msj_secundario'),
      contextualRedirect: () => {
        dispatch(updateMenuFavorites(2));
      },
      addSuccesMsg: t('lb_agregar_linea_exito_msj'),
      store: favoriteLines,
      formater: (id, name, alias, categoryId) => {
        return {
          routeId: id,
          alias: alias !== undefined ? alias : name,
          categoryId: categoryId !== undefined ? categoryId : null,
        };
      },
      deleteStore: (id) => dispatch(deleteLinesFavorites(id)),
      addStore: (id) => dispatch(addLinesFavorites(id)),
    },
    pois: {
      getId: () => 'poiId',
      deleteErrorMsg: t('lb_borrar_poi_error_msj'),
      addErrorMsg: t('lb_agregar_poi_error_msj'),
      deleteSuccessMsg: t('lb_borrar_poi_exito_msj'),
      secondarySuccessMsg: t('lb_borrar_poi_exito_msj_secundario'),
      contextualRedirect: () => {
        dispatch(updateMenuFavorites(1));
      },
      addSuccesMsg: t('lb_agregar_poi_exito_msj'),
      store: favoritePois,
      formater: (id, name, alias, categoryId) => {
        return {
          poiId: id,
          alias: alias !== undefined ? alias : name,
          categoryId: categoryId !== undefined ? categoryId : null,
        };
      },
      deleteStore: (id) => dispatch(deletePoisFavorites(id)),
      addStore: (id) => dispatch(addFavoritePois(id)),
    },
    events: {
      getId: () => 'eventId',
      deleteErrorMsg: t('lb_borrar_evento_error_msj'),
      addErrorMsg: t('lb_agregar_evento_error_msj'),
      deleteSuccessMsg: t('lb_borrar_evento_exito_msj'),
      secondarySuccessMsg: t('lb_borrar_evento_exito_msj_secundario'),
      contextualRedirect: () => {
        dispatch(updateMenuFavorites(3));
      },
      addSuccesMsg: t('lb_agregar_evento_exito_msj'),
      store: favoriteEvents,
      formater: (id, name, alias, categoryId) => {
        return {
          eventId: id,
          alias: alias !== undefined ? alias : name,
          categoryId: categoryId !== undefined ? categoryId : null,
        };
      },
      deleteStore: (id) => dispatch(deleteEventsFavorites(id)),
      addStore: (id) => dispatch(addFavoriteEvents(id)),
    },
    directions: {
      getId: () => 'id',
      deleteErrorMsg: t('lb_borrar_destino_error_msj'),
      addErrorMsg: t('lb_agregar_destino_error_msj'),
      deleteSuccessMsg: t('lb_borrar_destino_exito_msj'),
      secondarySuccessMsg: t('lb_borrar_destino_exito_msj_secundario'),
      contextualRedirect: () => {
        dispatch(updateMenuFavorites(1));
      },
      addSuccesMsg: t('lb_agregar_destino_exito_msj'),
      store: favoriteDestinations,
      formater: (name, alias, latitude, longitude, typeId, street) => {
        return {
          name,
          alias: alias ? textFormater(alias) : textFormater(name),
          street,
          latitude,
          longitude,
          typeId: typeId || 0,
        };
      },
      deleteStore: (id) => dispatch(deleteDirectionsFavorites(id)),
      addStore: async (id) => {
        await GetFavorites().then((favs) => {
          if (favs.isSuccess) {
            dispatch(updateFavoritesDirections(favs.data?.directions));
          }
        });
      },
    },
    default: {
      deleteErrorMsg: t('lb_msj_error_generico'),
      addErrorMsg: t('lb_msj_error_generico'),
      deleteSuccessMsg: t('lb_msj_exito_generico'),
      addSuccesMsg: t('lb_msj_exito_generico'),
      favorites: [],
      deleteStore: () => null,
      addStore: () => null,
    },
  };

  const isFavorite = (type, id, position?) => {
    if(type === 'directions' || type === TYPES_MARKERS.NOMINATIM) return favorites['directions'].store?.some((d)=> (Number(d.id) === Number(id)) || (d.latitude === position?.lat && d.longitude === position?.lng));
    if (id) return favorites[type].store?.some((fav) => fav[favorites[type].getId(id)] === id);
    return false;
  };

  const deleteFavorites = (type, idArr, hideModal) => {
    updateShowLoading(true);
    DeleteFavorites({
      [type]: [...idArr],
    })
      .then((response: any) => {
        if (response.data) {
          logEventAnalytics('delete_favorite', {type: type, id: idArr[0]?.id});
          favorites[type].contextualRedirect();
          idArr.forEach((id) => favorites[type].deleteStore(id));
          hideModal();
          dispatch(
            updateContextualMessage({ message: favorites[type].deleteSuccessMsg, type: 'success' }),
          );
        } else {
          hideModal();
          dispatch(
            updateContextualMessage({ message: favorites[type].deleteErrorMsg, type: 'error' }),
          );
        }
      })
      .catch(() => {
        hideModal();
        dispatch(
          updateContextualMessage({ message: favorites[type].deleteErrorMsg, type: 'error' }),
        );
      })
      .finally(() => updateShowLoading(false));
  };

  const saveFavorites = async (type, dataArr, hideModal) => {
    let data = dataArr;
    try {
      if(type === 'directions' || type === TYPES_MARKERS.NOMINATIM) {
        data = dataArr?.map((item) =>
          favorites[type].formater(item.name, item.alias, item.position?.lat, item.position?.lng, item.typeId, item.street),
        );
        logEventAnalytics('save_favorite', {type: type, lat: dataArr[0]?.position?.lat, lon: dataArr[0]?.position?.lng});
      } else {
        data = dataArr?.map((item) =>
          favorites[type].formater(item.id, item.name, item.alias, item.categoryId),
        );
        logEventAnalytics('save_favorite', {type: type, id: dataArr[0].id});
      };

      await PostFavorites({
        [type]: data,
      })
        .then(async (response: any) => {
          if (response.data) {
            data.forEach((item) => favorites[type].addStore(item));
            dispatch(
              updateContextualMessage({ message: favorites[type].addSuccesMsg, type: 'success' }),
            );
            favorites[type].contextualRedirect();
            if(type !== 'directions' && type !== 'events') {
              dispatch(
                updateSecondaryContextualMessage({
                  message: favorites[type].secondarySuccessMsg,
                  type: 'info',
                }),
              );
            }
            hideModal();
          } else {
            hideModal();
            dispatch(
              updateContextualMessage({ message: favorites[type].addErrorMsg, type: 'error' }),
            );
          }
        })
        .catch((e) => {
          console.error({postFavError: e});
          hideModal();
          dispatch(
            updateContextualMessage({ message: favorites[type].addErrorMsg, type: 'error' }),
          );
        })
        .finally(() => updateShowLoading(false));
    } catch (error) {
      console.error({postFavError: error});
      hideModal();
      dispatch(updateContextualMessage({ message: favorites[type].addErrorMsg, type: 'error' }));
      return error;
    }
  };

  return {
    isFavorite,
    deleteFavorites,
    saveFavorites,
  };
};
